import React, { useMemo, useRef, useState } from 'react';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import styles from './AbasDocuments.module.scss';
import { Input } from '../../../components/uikit/inputs/Input.tsx';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import { useWatch } from 'react-hook-form';
import { Button } from '../../../components/uikit/buttons/Button.tsx';
import clsx from 'clsx';
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from '../../../helpers/pagination-helper.ts';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import Cross from 'assets/icons/close-button-small.svg?react';
import { useTranslation } from 'react-i18next';
import { NegativeActionText } from '../../../components/uikit/NegativeActionText.tsx';
import { AbasFileQuery } from 'services/api/api-client.ts';
import { AppPagination } from '../../../components/uikit/pagination/AppPagination.tsx';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { abasUnavailableUrn, ErrObj } from '../../../helpers/abas-helper.ts';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';
import { AccordionHeader } from '../../../components/uikit/AccordionHeader.tsx';
import { Checkbox } from '../../../components/uikit/inputs/Checkbox.tsx';
import type { AbasFileSearchResultDto } from '../../../services/api/api-client.types.ts';
import { Accordion } from './components/Accordion';
import { useTriggerOnClickOutsideElement } from '../../../helpers/useTriggerOnClickOutsideElement.ts';
import { Links } from '../../../application/constants/links.ts';

export const AbasDocumentsCategory = 'AbasDocuments';

type Form = {
  search: string;
  onlyMissingOnSharePoints: boolean;
};

export type AbasDocumentGroup = {
  infosystemTypeName: string;
  rows: AbasFileSearchResultDto[];
};

const pageSizes = [50, 100, 200];

const searchSuggestions = [
  {
    label: 'Service presentation',
    value: 'serviceppt',
  },
  {
    label: 'Service and Repair',
    value: 'reparaturanleitungen',
  },
  {
    label: 'Tech updates',
    value: 'fachinfo',
  },
  {
    label: 'Telehealth billing',
    value: 'tm',
  },
  {
    label: 'Price lists',
    value: 'preislisten',
  },
  {
    label: 'Commercial terms',
    value: 'kondiblatt',
  },
];

const columnsOfOuterTable = [
  {
    serverKey: '',
    translateKey: 'file_name',
    width: '40%',
    classname: styles.colHeaderFileName,
    isSortingTurnedOff: true,
  },
  {
    serverKey: '',
    translateKey: 'file_link',
    width: 'calc(60% - 212px)',
    classname: styles.colHeaderFileLink,
    isSortingTurnedOff: true,
  },
];

export const AbasDocuments = () => {
  const i18n = useTranslation('translation', {
    keyPrefix: 'Page.AbasDocuments',
  });
  const [closedGroups, setClosedGroups] = useState<Set<string>>(new Set());
  const [hoveredHeader, setHoveredHeader] = useState<string | null>(null);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const searchAutocompleteRef = useRef<HTMLDivElement>(null);

  const [params, setParams] = useQueryParams({
    search: StringParam,
    onlyMissingOnSharePoints: withDefault(BooleanParam, false),
    ...pagingSortingQueryParams(pageSizes[1]),
    sortBy: withDefault(StringParam, 'updatedAt'),
    desc: withDefault(BooleanParam, true),
  });

  const form = useAdvancedForm<Form>(
    async (data) => {
      setParams({ ...params, ...data, page: 1 });
      setIsSuggestionsOpen(false);
      openAllAccordions();
    },
    {
      defaultValues: {
        search: params.search ?? '',
        onlyMissingOnSharePoints: params.onlyMissingOnSharePoints ?? false,
      },
    },
  );

  const abasFilesData = AbasFileQuery.useSearchQuery(
    {
      search: params.search ?? '',
      onlyMissingOnSharePoint: params.onlyMissingOnSharePoints || undefined,
      ...pagingSortingToBackendRequest(params),
    },
    {
      enabled: true,
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  const fileGroups = useMemo<AbasDocumentGroup[]>(() => {
    return (abasFilesData.data?.fileGroups ?? []).map((group) => ({
      infosystemTypeName: group.infosystemTypeName,
      rows: group.files,
    }));
  }, [abasFilesData.data?.fileGroups]);

  const [search, onlyMissingOnSharePoints] = useWatch({
    control: form.control,
    name: ['search', 'onlyMissingOnSharePoints'],
  });

  const filteredSuggestions = useMemo(() => {
    const normalizedSearch = search?.trim().toLowerCase();
    if (!normalizedSearch) {
      return searchSuggestions;
    }

    return searchSuggestions.filter((suggestion) =>
      suggestion.label.toLowerCase().includes(normalizedSearch),
    );
  }, [search]);

  useTriggerOnClickOutsideElement(
    searchAutocompleteRef as React.RefObject<HTMLElement>,
    () => setIsSuggestionsOpen(false),
    isSuggestionsOpen,
  );

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));

    openAllAccordions();
  };

  const numberOfRows = fileGroups.reduce(
    (acc, group) => acc + group.rows.length,
    0,
  );

  const paginationProps = {
    page: params.page,
    perPage: params.perPage,
    totalCount: abasFilesData.data?.countOfRecords ?? 0,
    pageSizes: pageSizes,
    numberOfRows,
    changePagination: changePagination,
  };

  const resetFilters = () => {
    form.reset({
      search: '',
      onlyMissingOnSharePoints: false,
    });

    void form.handleSubmitDefault();
    setIsSuggestionsOpen(false);
    openAllAccordions();
  };

  const handleClickAccordion = (groupKey: string) => {
    setClosedGroups((prev) => {
      const next = new Set(prev);

      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }

      return next;
    });
  };

  const openAllAccordions = () => {
    setClosedGroups(new Set());
  };

  const closeAllAccordions = () => {
    const allKeys = fileGroups.map(
      (group, index) => `${group.infosystemTypeName}.${index}`,
    );
    setClosedGroups(new Set(allKeys));
  };

  const isResetButtonShown =
    !!params.search || !!params.onlyMissingOnSharePoints;

  const errObj = abasFilesData?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const isPlaceholderVisible =
    !numberOfRows &&
    !abasFilesData.isLoading &&
    !abasFilesData.isPlaceholderData &&
    !isExternalServiceUnavailable;

  const showSuggestions = isSuggestionsOpen && !!filteredSuggestions.length;

  return (
    <div data-test-id={'abas-documents-container'}>
      <Breadcrumbs customLinkForHomeIcon={Links.Authorized.Admin.route} />
      <Loading
        loading={abasFilesData.isLoading || abasFilesData.isPlaceholderData}
      >
        <form onSubmit={form.handleSubmitDefault}>
          <div className={styles.filterRowContainer}>
            <div
              ref={searchAutocompleteRef}
              className={styles.searchAutocompleteContainer}
            >
              <Input
                testId={'name-input'}
                {...form.register('search')}
                onChange={(e) => {
                  form.setValue('search', e.target.value);
                  setIsSuggestionsOpen(true);
                }}
                onFocus={() => {
                  setIsSuggestionsOpen(true);
                }}
                leftHelperText={i18n.t('search')}
                className={styles.width352}
                endAdornment={
                  <div>
                    {search && (
                      <IconButton
                        type={'destroy'}
                        onClick={() => {
                          form.setValue('search', '');
                          setIsSuggestionsOpen(false);
                        }}
                      >
                        <Cross />
                      </IconButton>
                    )}
                  </div>
                }
                endAdornmentClassname={styles.endAdornmentClassname}
              />
              {showSuggestions && (
                <div className={styles.searchSuggestionsDropdown}>
                  {filteredSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.value}
                      type={'button'}
                      className={styles.searchSuggestionItem}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        form.setValue('search', suggestion.value);
                        setIsSuggestionsOpen(false);
                      }}
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.checkboxRowContainer}>
              <Checkbox
                isChecked={onlyMissingOnSharePoints}
                onChange={(e) => {
                  form.setValue('onlyMissingOnSharePoints', e.target.checked);
                }}
                actionText={i18n.t('view_not_found_on_sp')}
                testId={'view-not-found-on-sp-checkbox'}
              />
            </div>
            <Button
              testId={'filters-apply-button'}
              type={'submit'}
              className={clsx(styles.actionButton)}
              title={i18n.t('apply')}
            />
            {isResetButtonShown && (
              <NegativeActionText
                testId={'filters-reset-button'}
                text={i18n.t('reset')}
                onClickHandler={resetFilters}
              />
            )}
          </div>
        </form>
        {!isExternalServiceUnavailable && (
          <>
            <AccordionHeader
              desc={false}
              sortBy={null}
              i18n={i18n as ReturnType<typeof useTranslation>}
              columnsOfOuterTable={columnsOfOuterTable}
              hoveredHeader={hoveredHeader}
              changeSortTable={() => undefined}
              setHoveredHeader={(value) => setHoveredHeader(value)}
              containerClassname={styles.accordionHeader}
            />
            <Accordion
              fileGroups={fileGroups}
              closedGroups={closedGroups}
              handleClickAccordion={handleClickAccordion}
              openAllAccordions={openAllAccordions}
              closeAllAccordions={closeAllAccordions}
            />
          </>
        )}
        {isExternalServiceUnavailable && (
          <AbasPlaceholder containerClassname={styles.marginTop152} />
        )}
        {isPlaceholderVisible && <TablePlaceholder />}
        {!!numberOfRows && <AppPagination {...paginationProps} />}
      </Loading>
    </div>
  );
};
